import { Button } from './primitives/Button.js';
import { Card, Container } from './primitives/Misc.js';
import { CONNECTOR_CATEGORIES } from '../data/connectors.js';
import './ConnectorDirectory.css';

export interface ConnectorDirectoryProps {
  onGoConnectorsDocs: () => void;
}

/**
 * The expanded "what it connects to" directory. Deliberately separate from
 * <Systems> above it: Systems answers "why does this matter to my
 * department", this answers "is my specific tool on the list" — the
 * question a buyer actually types into their browser's find-in-page.
 */
export function ConnectorDirectory({ onGoConnectorsDocs }: ConnectorDirectoryProps) {
  return (
    <Container className="land-section land-connectors">
      <div className="land-section__head">
        <div>
          <h2 className="land-section__title">Is your stack on the list?</h2>
          <p className="land-section__lede land-section__lede--wide">
            Native connectors — built by us, schema discovery included — for Salesforce, HubSpot
            and SharePoint today. Everywhere else, our generic connector authenticates however
            that vendor's own API allows: a real "click authorize" OAuth flow for the tools
            marked below, or a pasted API key / CSV upload for the rest. Same identity graph,
            same evidence, same 30-day rollback either way.
          </p>
        </div>
        <Button variant="secondary" size="sm" onClick={onGoConnectorsDocs}>
          Full connector documentation →
        </Button>
      </div>

      <div className="land-connectors__grid">
        {CONNECTOR_CATEGORIES.map((category) => (
          <Card key={category.id} className="land-connectors__card">
            <div className="land-mono-label">{category.label}</div>
            <p className="land-connectors__blurb">{category.blurb}</p>
            <div className="land-connectors__tools">
              {category.tools.map((tool) => (
                <span
                  key={tool.name}
                  className={`land-tag${tool.native ? ' land-connectors__tool--native' : tool.oauth ? ' land-connectors__tool--oauth' : ''}`}
                >
                  {tool.name}
                  {tool.native && <span className="land-connectors__native-dot" aria-hidden="true" />}
                  {!tool.native && tool.oauth && (
                    <span className="land-connectors__oauth-dot" aria-hidden="true" />
                  )}
                </span>
              ))}
            </div>
          </Card>
        ))}
      </div>

      <div className="land-connectors__bring-own">
        <Card tone="tint" className="land-connectors__bring-own-card">
          <div className="land-mono-label">Custom REST API</div>
          <p>
            Point us at any authenticated JSON endpoint — internal tool, homegrown database view,
            a vendor with no Dedupr connector yet. You map the fields once; matching, evidence and
            rollback work identically.
          </p>
        </Card>
        <Card tone="tint" className="land-connectors__bring-own-card">
          <div className="land-mono-label">CSV / XLSX upload</div>
          <p>
            No API, no problem. Drop a spreadsheet export, map the columns, and it's a pseudo-source
            in the same identity graph as everything else — delete it whenever you like.
          </p>
        </Card>
      </div>
      <div className="land-connectors__legend">
        <span className="land-connectors__native-dot" aria-hidden="true" /> native connector, built
        by us &nbsp;·&nbsp; <span className="land-connectors__oauth-dot" aria-hidden="true" /> OAuth
        via our generic connector &nbsp;·&nbsp; everything else: API key or CSV
      </div>
    </Container>
  );
}
